import { z } from 'zod'
import type { ChatInspectorIdentity, WorkspaceCanvasIdentity } from '@/features/conversation/ConversationShell'

const searchValue = z.union([z.string(), z.number(), z.array(z.string())]).optional()

// Every identifier that reaches a loader comes through here, so the fencing is the
// boundary rather than a convenience: anything that is not a positive safe integer is
// not an id, and an array is a repeated query parameter rather than a value.
function positiveSearchID(value: unknown): number | undefined {
  if (Array.isArray(value)) return undefined
  const number =
    typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
  return Number.isSafeInteger(number) && number > 0 ? number : undefined
}

// A chat that has not been created yet. It is a value rather than an identifier because
// there is nothing to identify: the chat is created by its first message, and until then
// this word is the whole of what the URL has to carry.
export const draftChat = 'new'

function chatSelection(value: unknown): number | typeof draftChat | undefined {
  return value === draftChat ? draftChat : positiveSearchID(value)
}

// Which corpus a content address names. An absent scope is the workspace, so a link
// written before the organisation had content of its own still means what it meant. A
// scope that is present and is neither of the two voids the address the same way an
// unreadable id does: the link named a scope, and this is not one. An array is a repeated
// query parameter rather than a value, and is caught by the same comparison.
function contentScope(value: unknown): 'workspace' | 'global' | undefined {
  if (value === undefined) return 'workspace'
  return value === 'workspace' || value === 'global' ? value : undefined
}

function kindAndID(value: unknown): { kind: string; id: string } | undefined {
  if (typeof value !== 'string') return undefined
  const separator = value.indexOf(':')
  if (separator <= 0 || separator === value.length - 1) return undefined
  return { kind: value.slice(0, separator), id: value.slice(separator + 1) }
}

// The URL is the state. These parameters are exactly what a link carries, and the
// identities below are a projection of them, not a second representation stored beside
// them: a parsed object in `location.search` would be re-serialised into the URL as
// JSON the next time anything navigated.
export type WorkspaceSearch = {
  chat?: number | typeof draftChat
  doc?: string
  section?: number
  slide?: number
  version?: number
  // Written only for the organisation's corpus. The workspace is what an address carrying
  // no scope already means, so spelling it out would put a parameter in every content
  // link to say what its absence says.
  scope?: 'global'
  inspect?: string
}

// Three independent concerns, so each drops on its own. The chat route's schema
// collapsed the entire URL when any part of the one identity it carried was invalid,
// which is right for one identity and wrong for three: a malformed inspector should not
// close the document beside it.
export const workspaceSearchSchema = z
  .object({
    chat: searchValue,
    doc: searchValue,
    section: searchValue,
    slide: searchValue,
    version: searchValue,
    scope: searchValue,
    inspect: searchValue
  })
  .transform((raw): WorkspaceSearch => {
    const chat = chatSelection(raw['chat'])
    const canvas = canvasIdentity(raw)
    const inspector = inspectorIdentity(raw)
    return {
      ...(chat === undefined ? {} : { chat }),
      ...(canvas === undefined ? {} : canvasSearch(canvas)),
      ...(inspector === undefined ? {} : inspectorSearch(inspector))
    }
  })

export function canvasIdentity(raw: Record<string, unknown>): WorkspaceCanvasIdentity | undefined {
  const doc = kindAndID(raw['doc'])
  if (doc === undefined) return undefined
  if (doc.kind === 'collection') {
    return doc.id === 'proposals' || doc.id === 'presentations' || doc.id === 'content' || doc.id === 'connectors'
      ? { canvas: 'collection', collection: doc.id }
      : undefined
  }
  if (doc.kind === 'proposal') {
    const proposalId = positiveSearchID(doc.id)
    const sectionId = positiveSearchID(raw['section'])
    // A section is optional: a proposal with nothing drafted yet opens as a whole. A
    // section key that is present but unreadable still voids the address, because the
    // link named a section and this is not it.
    if (proposalId === undefined || (raw['section'] !== undefined && sectionId === undefined)) return undefined
    return sectionId === undefined ? { canvas: 'proposal', proposalId } : { canvas: 'proposal', proposalId, sectionId }
  }
  if (doc.kind === 'document') {
    const documentId = positiveSearchID(doc.id)
    // A document opens whole. A revision is chosen inside the canvas, so the URL carries
    // no version and an older revision cannot outlive the link that named it.
    return documentId === undefined ? undefined : { canvas: 'document', documentId }
  }
  // A content row opens whole, so none of these carries a part or a revision. The kind is
  // in the `doc` value rather than in a parameter of its own because a file and a text are
  // addressed by the same number on the Content page and only the kind says which of the
  // two a link meant. The scope is a parameter because it is the one thing beside the id
  // that a file or a text address needs, and it reads as one on a URL.
  if (doc.kind === 'file') {
    const fileId = positiveSearchID(doc.id)
    const scope = contentScope(raw['scope'])
    return fileId === undefined || scope === undefined ? undefined : { canvas: 'file', fileId, scope }
  }
  if (doc.kind === 'text') {
    const docId = positiveSearchID(doc.id)
    const scope = contentScope(raw['scope'])
    return docId === undefined || scope === undefined ? undefined : { canvas: 'text', docId, scope }
  }
  if (doc.kind === 'connector') {
    const connectorId = positiveSearchID(doc.id)
    return connectorId === undefined ? undefined : { canvas: 'connector', connectorId }
  }
  if (doc.kind === 'presentation') {
    const presentationId = positiveSearchID(doc.id)
    if (presentationId === undefined) return undefined
    const slideId = positiveSearchID(raw['slide'])
    // A slide is addressed by the slide alone, and `version` is read only to be dropped.
    // The pair rule that used to void a lone `slide` was there to stop a slide loading at
    // an arbitrary version, and the panel never loaded a version in the first place: it
    // renders the slide's current content, so the version only ever fed a staleness guard
    // that turned every link stale one refine after it was written. Links already in the
    // wild carrying `version` therefore open the slide now instead of dead-ending.
    return slideId === undefined
      ? { canvas: 'presentation', presentationId }
      : { canvas: 'presentation', presentationId, slideId }
  }
  return undefined
}

export function inspectorIdentity(raw: Record<string, unknown>): ChatInspectorIdentity | undefined {
  const inspect = kindAndID(raw['inspect'])
  if (inspect === undefined) return undefined
  const id = positiveSearchID(inspect.id)
  if (id === undefined) return undefined
  if (inspect.kind === 'facts') return { inspector: 'facts', briefId: id }
  if (inspect.kind === 'sources') return { inspector: 'sources', messageId: id }
  if (inspect.kind === 'activity') return { inspector: 'activity', originMessageId: id }
  return undefined
}

// The other direction. Anything that opens a surface writes the URL through these, so
// there is one place that decides what a document's address looks like.
export function canvasSearch(
  canvas: WorkspaceCanvasIdentity
): Pick<WorkspaceSearch, 'doc' | 'section' | 'slide' | 'version' | 'scope'> {
  if (canvas.canvas === 'collection') return { doc: `collection:${canvas.collection}` }
  if (canvas.canvas === 'proposal') {
    return {
      doc: `proposal:${canvas.proposalId}`,
      ...(canvas.sectionId === undefined ? {} : { section: canvas.sectionId })
    }
  }
  if (canvas.canvas === 'document') return { doc: `document:${canvas.documentId}` }
  // Only the organisation scope is written: the workspace is what an address carrying no
  // scope already means, so a spelled-out one normalises away rather than riding along.
  if (canvas.canvas === 'file') {
    return { doc: `file:${canvas.fileId}`, ...(canvas.scope === 'global' ? { scope: canvas.scope } : {}) }
  }
  if (canvas.canvas === 'text') {
    return { doc: `text:${canvas.docId}`, ...(canvas.scope === 'global' ? { scope: canvas.scope } : {}) }
  }
  if (canvas.canvas === 'connector') return { doc: `connector:${canvas.connectorId}` }
  return {
    doc: `presentation:${canvas.presentationId}`,
    ...(canvas.slideId === undefined ? {} : { slide: canvas.slideId })
  }
}

export function inspectorSearch(inspector: ChatInspectorIdentity): Pick<WorkspaceSearch, 'inspect'> {
  return {
    inspect:
      inspector.inspector === 'facts'
        ? `facts:${inspector.briefId}`
        : inspector.inspector === 'sources'
          ? `sources:${inspector.messageId}`
          : `activity:${inspector.originMessageId}`
  }
}

// Clearing a surface has to name every parameter that surface owns, otherwise a stale
// `section`, `slide` or `scope` outlives the document it belonged to.
// `version` is no longer written by canvasSearch, and it stays in this list on purpose. A
// workspace URL normalises on parse, so it cannot arrive here carrying one, but this list
// is also applied to search coming from other routes (see ConversationWorkspaceShell's
// openCanvas), which have not been through this schema and can still carry a legacy
// version. Dropping it from the list would leave that one stuck in the URL.
export const canvasSearchKeys = ['doc', 'section', 'slide', 'version', 'scope'] as const
export const inspectorSearchKeys = ['inspect'] as const

export function switchChatSearch(
  current: Record<string, unknown>,
  conversationId: number | typeof draftChat
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...current, chat: conversationId }
  for (const key of inspectorSearchKeys) delete next[key]
  return next
}
